import {
    useDebouncedValue,
    useThrottledCallback,
    useThrottledValue,
} from "@mantine/hooks";
import {useEffect} from "react";

const setHighlights = (
    searchTerm: string | string[],
    selector: string = "[data-searchable]"
) => {
    if (!CSS.highlights) {
        console.log("CSS.highlights is not supported in this browser");
        return;
    }

    const cleanedSearchTerms = Array.isArray(searchTerm)
        ? searchTerm.map((t) => decodeURIComponent(t.trim().toLowerCase()))
        : [decodeURIComponent(searchTerm.trim().toLowerCase())];
    if (
        !cleanedSearchTerms.length ||
        !cleanedSearchTerms.some((t) => t.length > 0)
    ) {
        CSS.highlights.clear();
        console.log("No search term provided");
        return;
    }

    CSS.highlights.clear();

    const dataSearchable = document.querySelectorAll(selector);

    const allTextNodes = [];
    dataSearchable.forEach((element) => {
        const treeWalker = document.createTreeWalker(
            element,
            NodeFilter.SHOW_TEXT
        );
        let currentNode = treeWalker.nextNode();
        while (currentNode) {
            allTextNodes.push(currentNode);
            currentNode = treeWalker.nextNode();
        }
    });

    const ranges = allTextNodes
        .map<{el: Element; text: string}>((el) => ({
            el,
            text: el.textContent.toLowerCase(),
        }))
        .map(({text, el}) => {
            const indices = [];
            const ranges = [];
            for (const cleanedSearchTerm of cleanedSearchTerms) {
                let startPos = 0;
                while (startPos < text.length) {
                    const index = text.indexOf(cleanedSearchTerm, startPos);
                    if (index === -1) break;
                    indices.push(index);
                    startPos = index + cleanedSearchTerm.length;
                }
                // Create a range object for each instance of
                // cleanedSearchTerm we found in the text node.
                ranges.push(
                    ...indices.map((index) => {
                        const range = new Range();
                        range.setStart(el, index);
                        range.setEnd(el, index + cleanedSearchTerm.length);
                        return range;
                    })
                );
            }
            return ranges;
        })
        .flat();

    const searchResultsHighlight = new Highlight(...ranges);
    CSS.highlights.set("search-results", searchResultsHighlight);
};

export const useCSSHighlights = (
    searchTerm?: string | string[],
    selector: string = "[data-searchable]",
    deps: any[] = []
) => {
    const [value] = useDebouncedValue(searchTerm, 200);
    useEffect(() => {
        if (value && value.length > 0) {
            setHighlights(value, selector);
        } else {
            if (CSS.highlights) {
                CSS?.highlights.clear();
            }
        }
    }, [value, ...deps]);
};
